import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import {
  Loader2,
  RefreshCw,
  ExternalLink,
  AlertCircle,
  Clock,
  History,
} from 'lucide-react';
import { MethodsTable, type MethodOverride } from '../components/MethodsTable';
import { ChangeLogList } from '../components/ChangeLogList';
import {
  useGetDiscoveredMethods,
  useStartDiscovery,
  useUpdateCuratedIntegration,
  useTriggerResync,
  useListIntegrationChanges,
  useGetCuratedIntegration,
} from '../../../../api/curated-integrations.api';
import type {
  CuratedIntegrationListItemDto,
  UpdateCuratedIntegrationDto,
  IntegrationChangeDto,
} from '../../../../api/curated-integrations.types';
import { toast } from 'sonner';

/** Polling interval (ms) while waiting for admin connection to become connected. */
const POLL_INTERVAL_MS = 5_000;
/** Maximum time (ms) to wait for a connection before showing a timeout hint. */
const POLL_TIMEOUT_MS = 60_000;

interface MethodsTabProps {
  /** The integration being managed. */
  integration: CuratedIntegrationListItemDto;
  /** Called after a successful save so the parent can refresh the integration. */
  onSaved: () => void;
}

/**
 * Tab "Methods" der Integration-Detail-Page.
 *
 * Zeigt je nach Verbindungsstatus:
 * - Discovery-Button (wenn nicht verbunden)
 * - Methoden-Tabelle mit Whitelist + Overrides (wenn verbunden)
 * - Re-Sync-Button + Änderungsprotokoll (wenn Published)
 */
export function MethodsTab({ integration, onSaved }: MethodsTabProps) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [isPolling, setIsPolling] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [enabledMethods, setEnabledMethods] = useState<string[]>([]);
  const [methodOverrides, setMethodOverrides] = useState<Record<string, MethodOverride>>({});
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(new Set());
  const [isSyncing, setIsSyncing] = useState(false);

  // ── Hooks ──────────────────────────────────────────────────────────────────
  const {
    data: integrationData,
    refetch: refetchIntegration,
  } = useGetCuratedIntegration(integration.id);

  const {
    data: methodsData,
    isLoading: methodsLoading,
    error: methodsError,
    refetch: refetchMethods,
  } = useGetDiscoveredMethods(integration.id);

  const {
    data: changesData,
    isLoading: changesLoading,
    refetch: refetchChanges,
  } = useListIntegrationChanges(integration.id);

  const { mutateAsync: startDiscovery, isLoading: discoveryLoading } = useStartDiscovery();
  const { mutateAsync: updateIntegration, isLoading: saveLoading, error: saveError } = useUpdateCuratedIntegration();
  const { mutateAsync: triggerResync } = useTriggerResync();

  // ── Initialise enabled methods from discovered tools ───────────────────────
  useEffect(() => {
    if (methodsData?.tools) {
      const whitelisted = methodsData.tools
        .filter((t) => t.isWhitelisted)
        .map((t) => t.name);
      setEnabledMethods(whitelisted);
    }
  }, [methodsData]);

  // ── Polling for admin connection ──────────────────────────────────────────
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    setIsPolling(false);
  }, []);

  const startPolling = useCallback(() => {
    setIsPolling(true);
    setPollTimedOut(false);

    pollTimerRef.current = setInterval(async () => {
      try {
        await refetchIntegration();
      } catch {
        // ignore transient errors during poll
      }
    }, POLL_INTERVAL_MS);

    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      setPollTimedOut(true);
      toast.error(
        'Zeitüberschreitung: Die Admin-Verbindung konnte nicht hergestellt werden. Bitte versuchen Sie es erneut.',
      );
    }, POLL_TIMEOUT_MS);
  }, [refetchIntegration, stopPolling]);

  // Eagerly refetch when the OAuth popup posts back. The backend callback
  // page sends `window.postMessage({ type: 'mcp-oauth-complete' })` after
  // exchanging the authorization code. Without this listener admins would
  // have to wait for the next 5s poll tick before the UI flips.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string } | null;
      if (data && typeof data === 'object' && data.type === 'mcp-oauth-complete') {
        void refetchIntegration();
        void refetchMethods();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [refetchIntegration, refetchMethods]);

  // Stop polling when connection status becomes connected
  useEffect(() => {
    const adminStatus = (integrationData as (CuratedIntegrationListItemDto & { adminConnectionStatus?: string }) | undefined)?.adminConnectionStatus;
    if (isPolling && adminStatus === 'connected') {
      stopPolling();
      toast.success('Admin-Verbindung hergestellt. Methoden werden geladen…');
      void refetchMethods();
      onSaved();
    }
  }, [integrationData, isPolling, stopPolling, refetchMethods, onSaved]);

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleStartDiscovery = async () => {
    try {
      const result = await startDiscovery(integration.id);
      if (result.authorizationUrl) {
        window.open(result.authorizationUrl, '_blank', 'noopener,noreferrer');
        toast.info('Autorisierungsfenster geöffnet. Schließen Sie den OAuth-Flow ab und kehren Sie dann zurück.');
        startPolling();
      } else {
        // No auth URL — connection may already be established or not require OAuth
        toast.success('Discovery gestartet.');
        void refetchIntegration();
        void refetchMethods();
        onSaved();
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Starten der Discovery.';
      toast.error(message);
    }
  };

  const handleSaveMethods = async () => {
    const dto: UpdateCuratedIntegrationDto = {
      enabledMethods,
      methodOverrides: Object.fromEntries(
        Object.entries(methodOverrides).map(([k, v]) => [k, { label: v.label, description: v.description }]),
      ),
    };
    try {
      await updateIntegration({ id: integration.id, dto });
      toast.success('Methoden-Whitelist gespeichert.');
      onSaved();
    } catch {
      // saveError rendered below
    }
  };

  const handleResync = async () => {
    setIsSyncing(true);
    try {
      await triggerResync(integration.id);
      toast.success('Re-Sync ausgelöst. Methoden werden aktualisiert…');
      await refetchMethods();
      await refetchChanges();
      onSaved();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Re-Sync fehlgeschlagen.';
      toast.error(message);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleChangeAcknowledged = (logId: string) => {
    setAcknowledgedIds((prev) => new Set([...prev, logId]));
  };

  // ── Derived state ─────────────────────────────────────────────────────────
  const adminStatus = (integrationData as (CuratedIntegrationListItemDto & { adminConnectionStatus?: string }) | undefined)?.adminConnectionStatus
    ?? (integration as (CuratedIntegrationListItemDto & { adminConnectionStatus?: string })).adminConnectionStatus;
  const isConnected = adminStatus === 'connected';
  const methods = methodsData?.tools ?? [];

  const visibleChanges: IntegrationChangeDto[] = (changesData ?? []).filter(
    (c) => !acknowledgedIds.has(c.id) && !c.acknowledgedAt,
  );
  const hasChanges = visibleChanges.length > 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Discovery section — shown when not connected */}
      {!isConnected && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <ExternalLink className="w-5 h-5 text-blue-600" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Admin-Verbindung einrichten</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Um Methoden zu entdecken, muss zuerst eine Admin-Verbindung zum MCP-Server
                  hergestellt werden. Klicken Sie auf "Discovery starten" und autorisieren Sie den
                  Zugriff im geöffneten Fenster.
                </p>

                {pollTimedOut && (
                  <div
                    role="alert"
                    className="flex items-center gap-2 mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3"
                  >
                    <Clock className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    Zeitüberschreitung. Bitte starten Sie die Discovery erneut.
                  </div>
                )}

                {isPolling && (
                  <div className="flex items-center gap-2 mt-3 text-sm text-blue-700">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Warte auf Verbindungsbestätigung…
                  </div>
                )}

                <div className="flex items-center gap-3 mt-4">
                  <Button
                    type="button"
                    onClick={handleStartDiscovery}
                    disabled={discoveryLoading || isPolling}
                    aria-label="OAuth-Discovery starten"
                  >
                    {discoveryLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" aria-hidden="true" />
                    )}
                    Discovery starten
                  </Button>

                  {isPolling && (
                    <Button type="button" variant="outline" onClick={stopPolling} aria-label="Polling abbrechen">
                      Abbrechen
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Methods table — shown when connected */}
      {isConnected && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Methoden-Whitelist</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Wählen Sie die Methoden aus, die Kunden verwenden dürfen. Aktivierte Methoden:{' '}
                  <strong>{enabledMethods.length}</strong> / {methods.length}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {integration.isPublished && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResync}
                    disabled={isSyncing}
                    aria-label="Methoden-Re-Sync auslösen"
                  >
                    {isSyncing ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-1.5" aria-hidden="true" />
                    )}
                    Re-Sync
                  </Button>
                )}
              </div>
            </div>

            {methodsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" aria-label="Methoden werden geladen…" />
              </div>
            ) : methodsError ? (
              <div
                role="alert"
                className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                Fehler beim Laden der Methoden: {methodsError.message}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  onClick={() => void refetchMethods()}
                  aria-label="Methoden neu laden"
                >
                  Wiederholen
                </Button>
              </div>
            ) : (
              <MethodsTable
                methods={methods}
                enabledMethods={enabledMethods}
                methodOverrides={methodOverrides}
                onEnabledMethodsChange={setEnabledMethods}
                onMethodOverridesChange={setMethodOverrides}
              />
            )}

            {saveError && (
              <div
                role="alert"
                className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                {saveError.message}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleSaveMethods}
                disabled={saveLoading || methodsLoading}
                aria-label="Methoden-Whitelist und Overrides speichern"
              >
                {saveLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
                Speichern
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Change log — shown when published */}
      {integration.isPublished && (
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-gray-500" aria-hidden="true" />
                <h3 className="font-semibold text-gray-900">Änderungsprotokoll</h3>
                {hasChanges && (
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full"
                    aria-label={`${visibleChanges.length} nicht bestätigte Änderungen`}
                  >
                    {visibleChanges.length}
                  </span>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void refetchChanges()}
                aria-label="Änderungsprotokoll aktualisieren"
              >
                <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
              </Button>
            </div>

            {changesLoading ? (
              <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Lade Änderungen…
              </div>
            ) : (
              <ChangeLogList
                integrationId={integration.id}
                changes={visibleChanges}
                onAcknowledged={handleChangeAcknowledged}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
